import React, { useState, useEffect, useRef } from 'react';
import Modal from '../Modal';
import Button from '../Button';
import PermissionRow from './PermissionRow';

export interface ContainersPermissionSettings {
  General: 'Read'; // Only Read, no Write option
  BuildAndDeploy: 'None' | 'Read' | 'Write';
  EnvVars: 'None' | 'Read' | 'Write';
  Domain: 'None' | 'Read' | 'Write';
  HealthAndAlert: 'None' | 'Read' | 'Write';
}

export interface ContainersPermissionInput {
  Overview: 'Read'; // Only Read, no Write option
  Logs: 'Read'; // Only Read, no Write option
  Deployments: 'Read'; // Only Read, no Write option
  Metrics: 'Read'; // Only Read, no Write option
  Settings: ContainersPermissionSettings;
  Actions: {
    Deploy: 'Read' | 'Write';
  };
}

interface CollaboratorPermissionModalProps {
  open: boolean;
  email: string;
  onClose: () => void;
  onConfirm: (permissions: ContainersPermissionInput) => void;
  defaultPermissions?: ContainersPermissionInput;
  customPermissions?: ContainersPermissionInput;
  error?: string;
  isSubmitting?: boolean;
}

const defaultFeaturesPermissions: ContainersPermissionInput = {
  Overview: 'Read',
  Logs: 'Read',
  Deployments: 'Read',
  Metrics: 'Read',
  Settings: {
    General: 'Read',
    BuildAndDeploy: 'Write',
    EnvVars: 'Write',
    Domain: 'Write',
    HealthAndAlert: 'Write',
  },
  Actions: {
    Deploy: 'Write',
  },
};

const settingsFeatures = [
  { key: 'BuildAndDeploy', label: 'Build & Deploy', description: 'Configure build and deployment settings', readOnly: false },
  { key: 'EnvVars', label: 'Environment Variables', description: 'Manage environment variables', readOnly: false },
  { key: 'Domain', label: 'Domain', description: 'Manage custom domains', readOnly: false },
  { key: 'HealthAndAlert', label: 'Health and Alert', description: 'Configure health checks and alerts', readOnly: false },
];

const CollaboratorPermissionModal = ({
  open,
  email,
  onClose,
  onConfirm,
  defaultPermissions = defaultFeaturesPermissions,
  customPermissions,
  error,
  isSubmitting = false,
}: CollaboratorPermissionModalProps) => {
  const [isDefault, setIsDefault] = useState(!customPermissions);
  const [permissions, setPermissions] = useState<ContainersPermissionInput>(
    customPermissions || defaultPermissions
  );
  const prevOpenRef = useRef(open);
  const prevCustomPermissionsRef = useRef(customPermissions);

  // Reset permissions only when modal transitions from closed to open, or when customPermissions changes
  // Don't reset during submission or when modal is already open
  // Reset on error (when error appears after a failed request)
  useEffect(() => {
    const wasClosed = !prevOpenRef.current;
    const isNowOpen = open;
    const customPermissionsChanged = prevCustomPermissionsRef.current !== customPermissions;
    
    // Only reset if:
    // 1. Modal just opened (was closed, now open) AND not submitting
    // 2. OR customPermissions changed AND modal is open AND not submitting
    // 3. OR error appeared (request failed) - reset to original permissions
    const shouldReset = (wasClosed && isNowOpen && !isSubmitting) || 
                       (customPermissionsChanged && open && !isSubmitting) ||
                       (error && !isSubmitting && prevOpenRef.current === open);
    
    if (shouldReset) {
      setIsDefault(!customPermissions);
      setPermissions(customPermissions || defaultPermissions);
    }
    
    prevOpenRef.current = open;
    prevCustomPermissionsRef.current = customPermissions;
  }, [open, customPermissions, defaultPermissions, isSubmitting, error]);

  const handlePermissionChange = (
    section: 'main' | 'settings' | 'actions',
    key: string,
    value: 'None' | 'Read' | 'Write'
  ) => {
    const newPermissions = { ...permissions };
    
    if (section === 'main') {
      (newPermissions as any)[key] = value;
    } else if (section === 'settings') {
      newPermissions.Settings = {
        ...newPermissions.Settings,
        [key]: value as 'None' | 'Read' | 'Write',
      };
    } else if (section === 'actions') {
      newPermissions.Actions = {
        ...newPermissions.Actions,
        [key]: value as 'Read' | 'Write',
      };
    }
    
    setPermissions(newPermissions);
  };

  const handleToggle = (value: 'Default' | 'Custom') => {
    setIsDefault(value === 'Default');
    if (value === 'Default') {
      setPermissions(defaultPermissions);
    }
  };

  const handleSave = () => {
    onConfirm(permissions);
  };

  return (
    <Modal open={open} onClose={onClose} minWidth="650px">
      <div className="flex flex-col gap-6 text-dark">
        <div>
          <div>{email}</div>
          <div className="text-sm text-dark-grey">
            Configure how this user can access the App features.
          </div>
        </div>

        <div className="">
          <div className="bg-light-grey/10 px-4 inline-block w-fit py-1 rounded rounded-bl-none rounded-br-none">Features</div>
          <div className="bg-light-grey/10 flex justify-end pr-4">
            <div className="relative inline-block w-[140px] h-[32px] bg-white border border-light-grey rounded">
              <div className={`absolute top-0 left-0 w-1/2 h-full bg-cta-green rounded transition-all duration-300 ease-in-out ${isDefault ? 'translate-x-0' : 'translate-x-full'}`}></div>
              <div className="relative flex h-full">
                <span className={`flex-1 flex cursor-pointer items-center justify-center text-xs font-semibold z-10 transition-colors duration-300 ${isDefault ? 'text-white' : 'text-dark'}`} onClick={() => handleToggle('Default')}>
                  Default
                </span>
                <span className={`flex-1 flex cursor-pointer items-center justify-center text-xs font-semibold z-10 transition-colors duration-300 ${!isDefault ? 'text-white' : 'text-dark'}`} onClick={() => handleToggle('Custom')}>
                  Custom
                </span>
              </div>
            </div>
          </div>

          <div className="max-h-[400px] overflow-y-auto bg-light-grey/10 pt-4">
          <div className="divide-y divide-light-grey/40">
            <PermissionRow
              label="Default App Access"
              description="Grants read access to features of the app: Overview, Logs, Deployments, Metrics, and General Settings."
              currentValue="Read"
              isDefault={isDefault}
              readOnly={true}
              permissionType="readOnly"
              section="main"
              keyName="DefaultAppAccess"
              onPermissionChange={handlePermissionChange}
            />
            {settingsFeatures.map((feature) => (
              <PermissionRow
                key={feature.key}
                label={feature.label}
                description={feature.description}
                currentValue={isDefault ? (permissions.Settings[feature.key as keyof ContainersPermissionSettings] as string) : (permissions.Settings[feature.key as keyof ContainersPermissionSettings] as string)}
                isDefault={isDefault}
                readOnly={feature.readOnly}
                permissionType={feature.readOnly ? 'readOnly' : 'noneReadWrite'}
                section="settings"
                keyName={feature.key}
                onPermissionChange={handlePermissionChange}
              />
            ))}
            <PermissionRow
              label="Deploy"
              description="Deploy actions"
              currentValue={isDefault ? permissions.Actions.Deploy : permissions.Actions.Deploy}
              isDefault={isDefault}
              readOnly={false}
              permissionType="readWrite"
              section="actions"
              keyName="Deploy"
              onPermissionChange={handlePermissionChange}
            />
          </div>
        </div>

        </div>
        {error && (
          <div className="text-xs text-error-red text-center mt-2">{error}</div>
        )}
        <div className="flex justify-end gap-2 pt-4 border-t border-light-grey/20">
          <Button
            type="secondary"
            className="px-4 py-2 text-sm border-light-grey text-dark"
            onClick={onClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            className="px-4 py-2 text-sm bg-cta-green"
            onClick={handleSave}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CollaboratorPermissionModal;

